import { MIN_ZOOM } from '~/components/AssetModalV2/Visualizer/ImageVisualizer/shared/constants';

const TRACKPAD_PIXEL_DELTA_THRESHOLD = 50;
const WHEEL_DELTA_MODE_LINE = 1;
const WHEEL_DELTA_MODE_PAGE = 2;
const WHEEL_DELTA_MODE_PIXEL = 0;
const EFFECTIVE_WHEEL_NOTCH_DELTA_PX = 100;
const WHEEL_LINES_PER_NOTCH = 3;

type ImageWheelGesture = 'none' | 'pan' | 'zoom';

type GetImageWheelGestureParams = {
  ctrlKey: boolean;
  deltaMode: number;
  deltaX: number;
  deltaY: number;
  zoom: number;
};

type GetNormalizedImageWheelZoomDeltaParams = {
  deltaMode: number;
  deltaY: number;
};

/**
 * The zoom calculation expects pixel-like wheel notches. Firefox commonly reports three lines per
 * notch, while page mode reports one page, so normalize both without flattening multi-notch input.
 */
export const getNormalizedImageWheelZoomDelta = ({
  deltaMode,
  deltaY,
}: GetNormalizedImageWheelZoomDeltaParams): number => {
  if (!Number.isFinite(deltaMode) || !Number.isFinite(deltaY) || deltaY === 0) {
    return 0;
  }

  if (deltaMode === WHEEL_DELTA_MODE_LINE) {
    return (deltaY / WHEEL_LINES_PER_NOTCH) * EFFECTIVE_WHEEL_NOTCH_DELTA_PX;
  }

  if (deltaMode === WHEEL_DELTA_MODE_PAGE) {
    return deltaY * EFFECTIVE_WHEEL_NOTCH_DELTA_PX;
  }

  return deltaY;
};

/**
 * Browsers expose wheel units but not the physical input device. Horizontal, fractional, or small
 * pixel deltas are the most consistent cross-browser trackpad signals; coarse pixel deltas preserve
 * mouse-wheel zoom for devices that report pixels instead of lines.
 */
export const getImageWheelGesture = ({
  ctrlKey,
  deltaMode,
  deltaX,
  deltaY,
  zoom,
}: GetImageWheelGestureParams): ImageWheelGesture => {
  if (![deltaMode, deltaX, deltaY, zoom].every(Number.isFinite) || zoom <= 0 || (deltaX === 0 && deltaY === 0)) {
    return 'none';
  }

  if (ctrlKey) {
    return 'zoom';
  }

  if (deltaMode !== WHEEL_DELTA_MODE_PIXEL) {
    return 'zoom';
  }

  const hasHorizontalMovement = deltaX !== 0;
  const hasFractionalMovement = !Number.isInteger(deltaX) || !Number.isInteger(deltaY);
  const hasSmallPixelDelta = Math.max(Math.abs(deltaX), Math.abs(deltaY)) < TRACKPAD_PIXEL_DELTA_THRESHOLD;
  const isLikelyTrackpad = hasHorizontalMovement || hasFractionalMovement || hasSmallPixelDelta;

  if (!isLikelyTrackpad) {
    return 'zoom';
  }

  return zoom > MIN_ZOOM ? 'pan' : 'none';
};
